import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { SimplePageTitle } from '@/components/layout/PageTitle';
import { ProjectRelativeRedirect } from '@/components/navigation';
import { checkCondition } from '@/components/permissions/helpers';
import React, { ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { IState } from '@/state/IState';
import { CommentThreadType, PermissionCheck, ProjectPermissions } from '@/types';
import { useUnreadThreadCount } from '@/utils/hooks';
import ProjectDashboard from '../project-dashboard/ProjectDashboard';
import LandingPageSetup from './LandingPageSetup/LandingPageSetup';
import theme from './ProjectDetails.module.scss';
import ProjectHistoryLog from './ProjectHistoryLog';
import ProjectIntegrations from './ProjectIntegrations/ProjectIntegrations';
import ProjectMessageThread from './ProjectMessageThread/ProjectMessageThread';
import ProjectSettings from './ProjectSettings';
import { ProjectSurvey } from './ProjectSurvey/ProjectSurvey';
import ProjectUserManagement from './UserManagement/ProjectUserManagement';
import {
    LoadingIndicator,
    ProjectFeatureToggleFlags,
    useProjectHasFeatureToggle,
    useProfileHasFeatureToggle,
    ProfileFeatureToggleFlags,
} from '@ygtechnologies/bygr-frontend-component-library';
import { Tabs } from '@ygtechnologies/rubus-ui';

type TabId =
    | 'dashboard'
    | 'landing-page'
    | 'settings'
    | 'users'
    | 'project-thread'
    | 'history-log'
    | 'integrations'
    | 'survey';
interface TabSetup {
    id: TabId;
    name: ReactElement;
    render: (options: { projectId: number }) => ReactElement;
    permissions: PermissionCheck<ProjectPermissions>;
    fullHeight?: boolean;
    notificationBadgeNumber?: number;
}

const tabs: TabSetup[] = [
    {
        id: 'dashboard',
        name: <Trans>Dashboard</Trans>,
        render: (options) => <ProjectDashboard {...options} />,
        permissions: 'canViewDashboard',
    },
    {
        id: 'settings',
        name: <Trans>Settings</Trans>,
        render: (options) => <ProjectSettings {...options} />, //canManageProject
        permissions: 'canManageProject',
    },
    {
        id: 'landing-page',
        name: <Trans>Landing page</Trans>,
        render: (options) => <LandingPageSetup {...options} />, //canEditLandingPage
        permissions: 'canEditLandingPage',
    },
    {
        id: 'users',
        name: <Trans>Users</Trans>,
        render: (options) => <ProjectUserManagement {...options} />, //AdminOnly
        permissions: 'isAdministrator',
    },
    {
        id: 'project-thread',
        name: <Trans>Messages</Trans>,
        render: (options) => <ProjectMessageThread {...options} />,
        permissions: 'canManageProject',
        fullHeight: true,
    },
    {
        id: 'history-log',
        name: <Trans>History log</Trans>,
        permissions: 'isAdministrator',
        render: (options) => <ProjectHistoryLog {...options} />,
    },
    {
        id: 'integrations',
        name: <Trans>Integrations</Trans>,
        permissions: 'isAdministrator',
        render: (options) => <ProjectIntegrations {...options} />,
    },
    {
        id: 'survey',
        name: <Trans>Survey</Trans>,
        permissions: 'isAdministrator',
        render: (options) => <ProjectSurvey {...options} />,
    },
];

interface ProjectDetailsProps {
    tab: TabId;
    projectId: string | number;
}

const ProjectDetails: React.FC<ProjectDetailsProps> = ({ tab, projectId }) => {
    const currentProfile = useSelector((state: IState) => state.global.activeProfile);
    const activeProject = useSelector((state: IState) => state.global.activeProject!);

    const showProjectIntegrations = useProjectHasFeatureToggle(ProjectFeatureToggleFlags.ShowProjectIntegrations);
    const showBuyerSurvey = useProfileHasFeatureToggle(ProfileFeatureToggleFlags.ShowProfileSurvey);

    let allowedTabs = tabs.filter((x) => checkCondition(activeProject.permissions, x.permissions));

    if (!showProjectIntegrations) {
        allowedTabs = allowedTabs.filter((x) => x.id !== 'integrations');
    }
    if (!showBuyerSurvey) {
        allowedTabs = allowedTabs.filter((x) => x.id !== 'survey');
    }

    let projectThreadTab = allowedTabs.find((x) => x.id === 'project-thread') ?? { notificationBadgeNumber: 0 };
    projectThreadTab.notificationBadgeNumber = useUnreadThreadCount()[CommentThreadType.Project];

    if (showBuyerSurvey === null && tab === 'survey') {
        return <LoadingIndicator />;
    }

    if (showProjectIntegrations === null && tab === 'integrations') {
        return <LoadingIndicator />;
    }

    if (!allowedTabs.find((x) => x.id === tab)) {
        if (allowedTabs.length === 0) {
            return <ProjectRelativeRedirect to="/residential-units" />;
        }

        return <ProjectRelativeRedirect to={`/${allowedTabs[0].id}`} />;
    }

    const activeTab = allowedTabs.find((x) => x.id === tab);
    const isFullHeight = tab === 'dashboard' || activeTab?.fullHeight;

    const activeContent = activeTab?.render({ projectId: +projectId });
    const tabButtons = allowedTabs.map((t) => (
        <Tabs.Tab
            key={t.id}
            id={t.id}
            as={Link}
            to={`/projects/${projectId}/${t.id}`}
            label={t.name}
            badge={t.notificationBadgeNumber}
        />
    ));

    return (
        <>
            <SimplePageTitle title={t`Project`} />

            {isFullHeight ? (
                <div className={theme.dashboardTabContainer}>
                    <Tabs.Container activeId={tab} variant="underline" sticky>
                        {tabButtons}
                        <Tabs.Content>
                            <div className={theme.dashboardTabContent}>{activeContent}</div>
                        </Tabs.Content>
                    </Tabs.Container>
                </div>
            ) : (
                <Tabs.Container activeId={tab} variant="underline" sticky>
                    {tabButtons}
                    <Tabs.Content>{activeContent}</Tabs.Content>
                </Tabs.Container>
            )}
        </>
    );
};

export default ProjectDetails;
